import { useMemo, useEffect } from "react";
import type { printOnDemand } from "common/utils/models";
import {
  usePrintOnDemandProducts,
  useDefaultProductInitialization
} from "@design-lab/entities/product";
import { useCompositionForm } from "@design-lab/entities/composition";
import { useWarmChooseSizesPage } from "@design-lab/entities/artwork";

export type UseProductsLoaderParams = {
  catalogIds: string[];
  isSample: boolean;
  hasDesignProject: boolean;
  isLoadingProjectData: boolean;
  isEditMode: boolean;
  hasRestoredRef: React.MutableRefObject<boolean>;
  hasCatalogFromUrl: boolean;
  shouldLoad: boolean;
};

export type UseProductsLoaderReturn = {
  productsMap: Map<string, printOnDemand.getProduct.Composition & { catalogId: string }>;
  isLoadingProducts: boolean;
  allProductsLoaded: boolean;
  defaultProduct: printOnDemand.getProduct.Response | undefined;
  warmCache: (productId: string) => void;
};

export function useProductsLoader(params: UseProductsLoaderParams): UseProductsLoaderReturn {
  const {
    catalogIds,
    isSample,
    hasDesignProject,
    isLoadingProjectData,
    isEditMode,
    hasRestoredRef,
    hasCatalogFromUrl,
    shouldLoad
  } = params;

  const { form: compositionForm } = useCompositionForm();
  const warmChooseSizesPage = useWarmChooseSizesPage();

  // Load products for all catalog IDs
  const productsQueries = usePrintOnDemandProducts(shouldLoad ? catalogIds : [], isSample);

  // Get default product only if we're NOT loading a project and have no catalog IDs
  // This prevents defaulting to G500/White when we have an actual project
  // Important: Don't use default product if we're still loading project data
  // CRITICAL: Only use default if we're actually trying to load (shouldLoad = true)
  const shouldUseDefaultProduct =
    shouldLoad &&
    !hasDesignProject &&
    !isLoadingProjectData &&
    catalogIds.length === 0 &&
    !hasCatalogFromUrl;

  // Always call the hook, but pass flags to prevent it from actually loading
  // IMPORTANT: If shouldLoad is false, we should not initialize ANY product
  const { defaultProduct, isLoadingDefaultProduct } = useDefaultProductInitialization({
    hasDesignProject: shouldLoad ? !shouldUseDefaultProduct : true, // If not loading, pretend we have project
    isLoadingProjectData: shouldLoad ? !shouldUseDefaultProduct : true, // If not loading, pretend we're loading
    isEditMode,
    hasRestoredRef,
    compositionForm,
    hasCatalogFromUrl
  });

  // Override the result if we shouldn't use default
  const actualDefaultProduct = shouldUseDefaultProduct ? defaultProduct : undefined;
  const actualIsLoadingDefault = shouldUseDefaultProduct ? isLoadingDefaultProduct : false;

  // Build products map with composition data
  const productsMap = useMemo(() => {
    const map = new Map<string, printOnDemand.getProduct.Composition & { catalogId: string }>();

    productsQueries.forEach(query => {
      const productData = query.data;
      if (!productData?.compositions) return;

      productData.compositions.forEach((comp: printOnDemand.getProduct.Composition) => {
        map.set(comp.id, {
          ...comp,
          catalogId: productData.catalogNumber
        });
      });
    });

    return map;
  }, [productsQueries]);

  // Calculate loading states
  const isLoadingProducts =
    productsQueries.some(query => query.isLoading) || actualIsLoadingDefault;

  // Calculate allProductsLoaded without nested ternaries
  let allProductsLoaded = false;
  if (hasDesignProject) {
    allProductsLoaded = catalogIds.length > 0 && productsQueries.every(query => query.isSuccess);
  } else if (shouldUseDefaultProduct) {
    allProductsLoaded = !!actualDefaultProduct;
  } else if (catalogIds.length > 0) {
    allProductsLoaded = productsQueries.every(query => query.isSuccess);
  }
  // else remains false - Don't mark as loaded if we have no catalogs and aren't using default

  // Warm cache when products are loaded
  useEffect(() => {
    if (!allProductsLoaded || !shouldLoad || catalogIds.length === 0) return;

    productsQueries.forEach(({ data }) => {
      if (!data?.id) return;
      warmChooseSizesPage(data.id);
    });
  }, [allProductsLoaded, shouldLoad, catalogIds.length, productsQueries, warmChooseSizesPage]);

  return {
    productsMap,
    isLoadingProducts,
    allProductsLoaded,
    defaultProduct: actualDefaultProduct,
    warmCache: warmChooseSizesPage
  };
}
